import React, { useState, useEffect } from "react";
import { Text, Box } from "@showitinc/showit-wc";
import Modal from "../../app2components/base/Modal/Modal";
import styles from "./MoveSitesModal.module.css";
import { sortFoldersAlphabetically } from "@/utils/siteFolderSortingUtil";

const FolderRadioOption = ({ folder, isSelected, onClick }) => {
  const { folderId, name } = folder;

  return (
    <button
      type="button"
      className={`${styles["menu-item"]} ${isSelected ? styles.selected : ""}`}
      onClick={onClick}
      data-testid={`move-sites-folder-option-${folderId}`}
    >
      <div className={`${styles["radio-button"]} ${isSelected ? styles.selected : ""}`}></div>
      <span
        className={`${styles["folder-name"]} ${name === "Uncategorized" ? styles.uncategorized : ""}`}
      >
        {name}
      </span>
    </button>
  );
};

/**
 * Modal to move sites from a folder before deletion
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when sites are moved and folder can be deleted
 * @param {string} props.folderName - Name of the folder being deleted
 * @param {Array} props.sitesInFolder - Array of sites in the folder
 * @param {Array} props.availableFolders - Array of available folders to move sites to
 */
const MoveSitesModal = ({
  isOpen,
  onClose,
  onConfirm,
  folderName,
  sitesInFolder = [],
  availableFolders = [],
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFolderId(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedFolderId !== null) {
      onConfirm(selectedFolderId);
      onClose();
    }
  };

  const handleFolderSelect = (folderId) => {
    setSelectedFolderId(folderId);
  };

  const handleClose = () => {
    setSelectedFolderId(null);
    onClose();
  };

  const transformedFolders = availableFolders
    .filter((folder) => folder.name !== folderName)
    .map((folder) => ({
      folderId: folder.folderId,
      name: folder.name,
      label: folder.name,
    }));

  const sortedFolders = sortFoldersAlphabetically(transformedFolders);
  const hasUncategorized = sortedFolders.some((folder) => folder.name === "Uncategorized");
  const foldersWithUncategorized = hasUncategorized
    ? sortedFolders
    : [
        ...sortedFolders,
        { folderId: "uncategorized", name: "Uncategorized", label: "Uncategorized" },
      ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Move Sites Before Deleting Folder"
      size="medium"
      secondaryButtonTitle="Cancel"
      secondaryButtonAction={handleClose}
      primaryButtonTitle="Move Sites & Delete Folder"
      primaryButtonAction={handleConfirm}
      primaryButtonVariant="destructive"
      primaryButtonDisabled={selectedFolderId === null}
    >
      <div className={styles["modal-content"]}>
        <div className={styles["content-header"]}>
          <Text variant="header-2">
            The folder <strong>{folderName}</strong> contains {sitesInFolder.length} site
            {sitesInFolder.length !== 1 ? "s" : ""}.
          </Text>
          <Box pt="3">
            <Text variant="body-1" className={styles["note-text"]}>
              Please select a folder to move these sites to before deleting the folder:
            </Text>
          </Box>
        </div>

        <div className={styles["folder-list-container"]}>
          <div className={styles["folder-list"]}>
            {foldersWithUncategorized.map((folder) => (
              <FolderRadioOption
                key={folder.folderId}
                folder={folder}
                isSelected={selectedFolderId === folder.folderId}
                onClick={() => handleFolderSelect(folder.folderId)}
              />
            ))}
            {foldersWithUncategorized.length === 0 && (
              <div className={styles["no-folders-message"]}>
                <Text variant="body-1" className={styles["note-text"]}>
                  No other folders available. Create a new folder first.
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MoveSitesModal;
